import { useCallback, useEffect, useRef, useState } from "react";
import { logout } from "services/session";

export const IDLE_TIMEOUT_MS = 15 * 60 * 1000;
export const IDLE_LOGOUT_COUNTDOWN_SECONDS = 60;

// Shared through localStorage so activity in one tab keeps every open tab signed in.
const LAST_ACTIVITY_STORAGE_KEY = "last_activity_at";
const CHECK_INTERVAL_MS = 1000;
// mousemove fires dozens of times a second; one write a second is plenty against a 15-minute timeout.
const ACTIVITY_WRITE_THROTTLE_MS = 1000;

const readLastActivity = () =>
  Number(localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY)) || Date.now();

export default function useIdleLogout() {
  const [secondsLeft, setSecondsLeft] = useState(null);
  const isLoggingOut = useRef(false);
  const isWarningOpen = useRef(false);
  const lastActivityWrite = useRef(0);

  const markActive = useCallback(() => {
    const now = Date.now();
    if (now - lastActivityWrite.current < ACTIVITY_WRITE_THROTTLE_MS) return;
    lastActivityWrite.current = now;
    localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(now));
    isWarningOpen.current = false;
    setSecondsLeft(null);
  }, []);

  // Once the warning is up, only a click or a key dismisses it: browsers fire mousemove
  // when a dialog opens under a still cursor, which would cancel the logout on its own.
  const handleMouseMove = useCallback(() => {
    if (!isWarningOpen.current) markActive();
  }, [markActive]);

  const logoutNow = useCallback(() => {
    isLoggingOut.current = true;
    logout();
  }, []);

  useEffect(() => {
    markActive();

    // Measured from a timestamp, not counted down, because browsers throttle timers in
    // background tabs and a sleeping laptop runs none at all.
    const check = () => {
      if (isLoggingOut.current) return;
      const idleMs = Date.now() - readLastActivity();
      const remainingMs =
        IDLE_TIMEOUT_MS + IDLE_LOGOUT_COUNTDOWN_SECONDS * 1000 - idleMs;

      if (remainingMs <= 0) {
        logoutNow();
        return;
      }
      isWarningOpen.current = idleMs >= IDLE_TIMEOUT_MS;
      setSecondsLeft(
        isWarningOpen.current ? Math.ceil(remainingMs / 1000) : null
      );
    };

    const intervalId = setInterval(check, CHECK_INTERVAL_MS);
    // Capture phase: plenty of controls stop propagation (RecordLink, row actions).
    document.addEventListener("click", markActive, true);
    document.addEventListener("keydown", markActive, true);
    document.addEventListener("mousemove", handleMouseMove, true);
    document.addEventListener("visibilitychange", check);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("click", markActive, true);
      document.removeEventListener("keydown", markActive, true);
      document.removeEventListener("mousemove", handleMouseMove, true);
      document.removeEventListener("visibilitychange", check);
    };
  }, [markActive, handleMouseMove, logoutNow]);

  return {
    isWarningOpen: secondsLeft !== null,
    secondsLeft,
    stayLoggedIn: markActive,
    logoutNow,
  };
}
